import { memo, useCallback, useState } from 'react'

import { Divider } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { ActionMenu, AnimatedButton } from '@shared/components/ContextMenu'
import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { useTeamDeleteMutation } from 'hooks/mutations/org/Team'
import { teamsOverviewRoute } from 'pages/NWPlatform/Organizations/Organization/Teams'
import { teamRoute } from 'pages/NWPlatform/Organizations/Organization/Teams/Team'
import { teamQueryKeys } from 'services/queryKeys'

const ActionsMenuRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const navigate = useNavigate({ from: teamRoute.fullPath })
  const { organizationId, teamId } = teamRoute.useParams()

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()

  const organizationTeamQuery = useQuery(teamQueryKeys.detail({ teamId }))
  const { data: team } = organizationTeamQuery

  const { mutate: deleteTeam } = useTeamDeleteMutation()

  const handleTeamDelete = useCallback(() => {
    if (!team) return

    setAnchorEl(undefined)

    deleteTeam(
      { teamId: team.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:My.Organization.nameDelete', { name: team.name }), {
            variant: 'success',
          })

          setTimeout(() => {
            navigate({
              to: teamsOverviewRoute.to,
              params: {
                organizationId,
              },
              replace: true,
            })
          }, 300)
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [deleteTeam, enqueueSnackbar, navigate, organizationId, t, team])

  return (
    <>
      <AnimatedButton
        open={!!anchorEl}
        IconButtonProps={{
          onClick: (e) => setAnchorEl(e.currentTarget),
          color: 'inherit',
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(undefined)
        }}
        title={team?.name}
        entity={t('entity:org.team', { count: 1 })}
      >
        <MenuItemCopyId onSuccess={() => setAnchorEl(undefined)} id={teamId} />

        <Divider component="li" />

        <MenuItemDelete staticLabel={t('domain:Organization.deleteTeam')} onConfirm={handleTeamDelete} />
      </ActionMenu>
    </>
  )
}

export const ActionsMenu = memo(ActionsMenuRaw)
